'use client'

import React, { useState } from 'react'
import { Button, useDocumentInfo, useFormFields, useFormModified } from '@payloadcms/ui'
import { errMessage } from '../../lib/errors'

/**
 * IL RIQUADRO DI PUBBLICAZIONE dei post scritti a mano per la Pagina (collection "Post Facebook",
 * primo campo della edit view, di tipo `ui`). Sta nel form e non nella barra dei controlli per la
 * stessa ragione della finestra di annullo della newsletter: quella barra è una riga di pulsanti, e
 * un riquadro che spiega non ci sta.
 *
 * Mostra lo STATO REALE del post rispetto alla Pagina — mai uscito, programmato, in corso, uscito,
 * ritirato, fallito — e accanto solo le azioni che quello stato consente. Ogni cosa che fa nasce
 * dallo stesso fatto: su Facebook un post uscito non si modifica a distanza, e cancellarlo non
 * riporta indietro like e commenti. Quindi:
 *
 *  1. RIFIUTA DI PARTIRE CON MODIFICHE NON SALVATE. Il testo che esce è quello scritto in tabella,
 *     non quello nel form: pubblicare una bozza appena riscritta e non salvata manderebbe la
 *     versione precedente, e la differenza fra le due sarebbe invisibile subito dopo.
 *  2. DICE CHE FORMA AVRÀ IL POST prima di mandarlo (foto / link con card / solo testo): sono tre
 *     risultati diversi sulla Pagina e la differenza la decidono due campi lontani fra loro.
 *  3. CHIEDE CONFERMA due volte per le cose che non si disfano: la ripubblicazione (che lascia due
 *     post sulla Pagina) e il RITIRO (che cancella davvero).
 *
 * Lo stato lo legge dai campi del form, che qui sono la verità: li scrive il server e dopo ogni
 * azione la pagina si ricarica. L'unico caso in cui il form mente è quando l'operatore ha appena
 * scritto qualcosa e non ha salvato — e quel caso è scritto in chiaro nel riquadro.
 */

const ENDPOINT = '/api/publish-social-post'

const styles: Record<string, React.CSSProperties> = {
  box: {
    alignItems: 'center',
    background: 'var(--theme-elevation-50)',
    border: '1px solid var(--theme-elevation-150)',
    borderRadius: 'var(--style-radius-s)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    justifyContent: 'space-between',
    padding: '0.6rem 0.9rem',
    width: '100%',
    marginBottom: '1.25rem',
  },
  warn: { background: 'var(--theme-warning-50)', border: '1px solid var(--theme-warning-250)' },
  text: { fontSize: '0.85rem', lineHeight: 1.45, minWidth: 0 },
  dirty: { display: 'block', marginTop: '0.25rem', color: 'var(--theme-warning-500)' },
  actions: { display: 'flex', flexShrink: 0, gap: '0.4rem' },
}

const refId = (value: unknown): null | number | string => {
  if (typeof value === 'number' || typeof value === 'string') return value
  if (value && typeof value === 'object') {
    const id = (value as { id?: unknown }).id
    if (typeof id === 'number' || typeof id === 'string') return id
  }
  return null
}

const formatWhen = (iso?: string): string => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const isPast = (iso?: string): boolean => {
  if (!iso) return false
  const t = new Date(iso).getTime()
  return Number.isFinite(t) && t <= Date.now()
}

type Action = 'publish' | 'retract' | 'unschedule'

export default function PublishSocialPost() {
  const { id } = useDocumentInfo()
  const modified = useFormModified()

  // Selettori separati che tornano primitive: un unico selettore che costruisce un oggetto
  // rirenderebbe a ogni battuta in qualunque campo (use-context-selector confronta per identità).
  const postId = useFormFields(([fields]) => fields?.postId?.value as string | undefined)
  const permalink = useFormFields(([fields]) => fields?.permalink?.value as string | undefined)
  const publishedAt = useFormFields(([fields]) => fields?.publishedAt?.value as string | undefined)
  const retractedAt = useFormFields(([fields]) => fields?.retractedAt?.value as string | undefined)
  const scheduledAt = useFormFields(([fields]) => fields?.scheduledAt?.value as string | undefined)
  const publishStatus = useFormFields(([fields]) => fields?.publishStatus?.value as string | undefined)
  const error = useFormFields(([fields]) => fields?.error?.value as string | undefined)
  const link = useFormFields(([fields]) => (fields?.link?.value as string | undefined)?.trim())
  const image = useFormFields(([fields]) => refId(fields?.image?.value))

  const [busy, setBusy] = useState(false)

  // In creazione non c'è id: il post si pubblica da una riga salvata, e dirlo qui evita la caccia
  // al pulsante che non c'è.
  if (!id) {
    return (
      <div style={styles.box}>
        <span style={styles.text}>
          Salva il post: il pulsante <strong>Pubblica su Facebook</strong> compare qui appena la riga esiste.
        </span>
      </div>
    )
  }

  const call = async (action: Action, force = false): Promise<void> => {
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, action, force }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        window.alert(`Errore: ${data?.error || res.status}`)
        return
      }
      if (data?.reason === 'already-published') {
        const confirmed = window.confirm(
          'Questo post è già sulla Pagina. Vuoi pubblicarne una NUOVA copia con il contenuto attuale?\n\nIl post precedente non verrà eliminato né modificato: sulla Pagina ne resteranno due.',
        )
        if (confirmed) await call('publish', true)
        return
      }
      if (action === 'retract') {
        window.alert(
          data?.alreadyGone
            ? "Sulla Pagina il post non c'era più: era già stato cancellato a mano. La riga è stata allineata."
            : 'Ritirato dalla Pagina ✓',
        )
      } else if (action === 'unschedule') {
        window.alert('Programmazione annullata: il post non partirà da solo.')
      } else {
        window.alert('Pubblicato su Facebook ✓')
      }
      window.location.reload()
    } catch (e: unknown) {
      window.alert(`Errore: ${errMessage(e)}`)
    }
  }

  const run = async (action: Action) => {
    // Il server lavora sulla riga salvata, non sul form: con modifiche pendenti uscirebbe la
    // versione di prima. Meglio un rifiuto che spiega, che un post giusto a metà.
    if (action === 'publish' && modified) {
      window.alert('Ci sono modifiche non salvate: su Facebook uscirebbe la versione salvata, non questa. Salva, poi pubblica.')
      return
    }
    if (action === 'retract') {
      const ok = window.confirm(
        'Il post verrà ELIMINATO dalla Pagina Facebook.\n\nNon si può annullare: like, commenti e condivisioni se ne vanno con lui, e chi lo aveva condiviso resterà con un link morto. La riga resta qui, come traccia di quello che era uscito.\n\nProcedo?',
      )
      if (!ok) return
    }
    setBusy(true)
    try {
      await call(action)
    } finally {
      setBusy(false)
    }
  }

  const shape = image
    ? 'Uscirà come post con FOTO (nessuna card di anteprima). Il link, se c\'è, resta in coda al testo come URL.'
    : link
      ? 'Uscirà come post con LINK: titolo e immagine della card li costruisce Facebook leggendo quella pagina.'
      : 'Uscirà come post di solo testo.'

  const live = Boolean(postId) && !retractedAt
  const retracted = Boolean(retractedAt)
  const failed = publishStatus === 'failed'
  const inFlight = publishStatus === 'publishing'
  const scheduled = Boolean(scheduledAt) && !live

  const message = live ? (
    <>
      Pubblicato{publishedAt ? ` il ${formatWhen(publishedAt)}` : ''}.{' '}
      {permalink ? (
        <a href={permalink} rel="noreferrer" target="_blank">
          Apri il post su Facebook
        </a>
      ) : null}{' '}
      Il testo non si modifica più: per cambiarlo, ritira il post e ripubblicalo, oppure duplica il documento.
    </>
  ) : retracted ? (
    <>
      Ritirato dalla Pagina il {formatWhen(retractedAt)}
      {publishedAt ? ` (era uscito il ${formatWhen(publishedAt)})` : ''}. I campi sono di nuovo modificabili: correggi e ripubblica.
      {error ? ` — ${error}` : ''}
    </>
  ) : inFlight ? (
    <>Pubblicazione in corso (o interrotta a metà: dopo due minuti si può riprovare).</>
  ) : scheduled ? (
    <>
      {isPast(scheduledAt)
        ? `La data programmata (${formatWhen(scheduledAt)}) è PASSATA: il post parte al prossimo controllo del worker, entro un minuto.`
        : `Uscirà da solo il ${formatWhen(scheduledAt)} (il worker controlla la coda a intervalli, quindi può partire fino a un minuto dopo).`}{' '}
      {shape}
      {failed && error ? ` Tentativo precedente non riuscito: ${error}` : ''}
    </>
  ) : failed ? (
    <>Ultimo tentativo non riuscito: {error || 'errore non registrato'}</>
  ) : (
    <>{shape}</>
  )

  return (
    <div style={{ ...styles.box, ...(failed || retracted ? styles.warn : {}) }}>
      <span style={styles.text}>
        {message}
        {modified ? <em style={styles.dirty}>Ci sono modifiche non salvate: valgono solo dopo il salvataggio.</em> : null}
      </span>
      <span style={styles.actions}>
        {scheduled ? (
          <Button buttonStyle="secondary" disabled={busy} onClick={() => run('unschedule')} size="small">
            Annulla programmazione
          </Button>
        ) : null}
        {live ? (
          <Button buttonStyle="secondary" disabled={busy} onClick={() => run('retract')} size="small">
            Ritira dalla Pagina
          </Button>
        ) : null}
        <Button buttonStyle="secondary" disabled={busy} onClick={() => run('publish')} size="small">
          {busy ? 'Attendi…' : live ? 'Pubblica di nuovo' : scheduled ? 'Pubblica ora' : 'Pubblica su Facebook'}
        </Button>
      </span>
    </div>
  )
}
